(function () {
  'use strict';

  if (window.UnifiedRulesEngine) {
    return;
  }

  const VERSION = '1.0.0';

  const LEGACY_DEBUG_EVENT_NAME =
    'unified:debug';

  const DEFAULT_DECISION =
    'allow';

  const MAX_RULES = 200;

  const SUPPORTED_ACTIONS = [
    'allow',
    'block',
    'enrich',
    'route'
  ];

  const SUPPORTED_OPERATORS = [
    'equals',
    'not_equals',
    'exists',
    'not_exists',
    'in',
    'not_in',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'greater_than',
    'greater_or_equal',
    'less_than',
    'less_or_equal',
    'matches',
    'truthy',
    'falsy'
  ];

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function clone(value) {
    if (
      value === null ||
      typeof value === 'undefined'
    ) {
      return value;
    }

    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  function isPlainObject(value) {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function isEmpty(value) {
    return (
      value === '' ||
      value === null ||
      typeof value === 'undefined'
    );
  }

  function normalizeString(value) {
    return String(
      value || ''
    ).trim();
  }

  function normalizeDecision(value) {
    const decision =
      normalizeString(value)
        .toLowerCase();

    if (
      decision === 'allow' ||
      decision === 'block'
    ) {
      return decision;
    }

    return DEFAULT_DECISION;
  }

  function normalizePriority(value) {
    const priority =
      Number(value);

    if (!Number.isFinite(priority)) {
      return 0;
    }

    return priority;
  }

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .start === 'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId === 'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .end === 'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta
      );
    }

    return null;
  }

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors
        .report === 'function'
    ) {
      window.UnifiedErrors.report(
        severity,
        code,
        message,
        details || {}
      );

      return;
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Rules Engine] ' +
        message,
      details || {}
    );
  }

  function emitLegacyEvent(
    type,
    data
  ) {
    const detail = {
      type: type,

      timestamp:
        new Date().toISOString(),

      data:
        data || {}
    };

    try {
      window.dispatchEvent(
        new CustomEvent(
          LEGACY_DEBUG_EVENT_NAME,
          {
            detail: detail
          }
        )
      );

      return detail;
    } catch (error) {
      return null;
    }
  }

  function emitEvent(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        reportError(
          'error',
          'rules_event_emit_failed',
          'Rules Engine could not emit an event through UnifiedBus.',
          {
            event_type: type,
            error: error.message
          }
        );
      }
    }

    return emitLegacyEvent(
      type,
      data
    );
  }

  function debugLog(
    message,
    data
  ) {
    const config =
      getConfig();

    if (!config.debug) {
      return;
    }

    console.groupCollapsed(
      '%c[Unified Rules Engine] ' +
        message,
      'color:#7D664B;font-weight:bold;'
    );

    if (data) {
      console.log(data);
    }

    console.groupEnd();
  }

  function getRulesConfiguration() {
    const config =
      getConfig();

    if (
      Array.isArray(config.rules)
    ) {
      return {
        enabled: true,
        defaultDecision:
          DEFAULT_DECISION,
        definitions:
          clone(config.rules)
      };
    }

    if (
      isPlainObject(config.rules)
    ) {
      return {
        enabled:
          config.rules.enabled !==
            false,

        defaultDecision:
          normalizeDecision(
            config.rules
              .defaultDecision ||
            config.rules
              .default_decision
          ),

        definitions:
          Array.isArray(
            config.rules.definitions
          )
            ? clone(
                config.rules
                  .definitions
              )
            : []
      };
    }

    return {
      enabled: true,
      defaultDecision:
        DEFAULT_DECISION,
      definitions: []
    };
  }

  function getConfiguredRules() {
    return getRulesConfiguration()
      .definitions;
  }

  function getPathValue(
    source,
    path
  ) {
    if (!path) {
      return undefined;
    }

    const parts =
      String(path)
        .split('.')
        .filter(function (part) {
          return part !== '';
        });

    let current =
      source;

    for (
      let index = 0;
      index < parts.length;
      index += 1
    ) {
      if (
        current === null ||
        typeof current === 'undefined'
      ) {
        return undefined;
      }

      current =
        current[parts[index]];
    }

    return current;
  }

  function resolveConditionValue(
    condition,
    scope
  ) {
    const field =
      normalizeString(
        condition &&
        (
          condition.field ||
          condition.path
        )
      );

    if (!field) {
      return undefined;
    }

    if (
      field === '$event' ||
      field === 'event'
    ) {
      return scope.event;
    }

    if (
      field.indexOf('context.') === 0
    ) {
      return getPathValue(
        scope.context,
        field.slice(8)
      );
    }

    if (
      field.indexOf('payload.') === 0
    ) {
      return getPathValue(
        scope.payload,
        field.slice(8)
      );
    }

    return getPathValue(
      scope.payload,
      field
    );
  }

  function valuesEqual(
    left,
    right
  ) {
    if (
      Number.isNaN(left) &&
      Number.isNaN(right)
    ) {
      return true;
    }

    return left === right;
  }

  function compareNumeric(
    left,
    right,
    operator
  ) {
    if (
      typeof left !== 'number' ||
      !Number.isFinite(left) ||
      typeof right !== 'number' ||
      !Number.isFinite(right)
    ) {
      return false;
    }

    if (operator === 'greater_than') {
      return left > right;
    }

    if (
      operator ===
      'greater_or_equal'
    ) {
      return left >= right;
    }

    if (operator === 'less_than') {
      return left < right;
    }

    if (
      operator ===
      'less_or_equal'
    ) {
      return left <= right;
    }

    return false;
  }

  function collectionContains(
    collection,
    expected
  ) {
    if (Array.isArray(collection)) {
      return collection.some(
        function (item) {
          return valuesEqual(
            item,
            expected
          );
        }
      );
    }

    if (typeof collection === 'string') {
      return collection.indexOf(
        String(expected)
      ) !== -1;
    }

    return false;
  }

  function matchesPattern(
    value,
    pattern,
    flags
  ) {
    if (typeof value !== 'string') {
      return false;
    }

    try {
      const expression =
        pattern instanceof RegExp
          ? pattern
          : new RegExp(
              String(pattern),
              flags || ''
            );

      return expression.test(value);
    } catch (error) {
      return false;
    }
  }

  function evaluateCondition(
    condition,
    scope,
    warnings
  ) {
    if (!isPlainObject(condition)) {
      warnings.push({
        code:
          'invalid_condition',
        condition:
          clone(condition)
      });

      return false;
    }

    const operator =
      normalizeString(
        condition.operator ||
        condition.op ||
        'equals'
      ).toLowerCase();

    if (
      SUPPORTED_OPERATORS.indexOf(
        operator
      ) === -1
    ) {
      warnings.push({
        code:
          'unsupported_operator',
        operator: operator,
        field:
          condition.field ||
          condition.path ||
          ''
      });

      return false;
    }

    const actual =
      resolveConditionValue(
        condition,
        scope
      );

    const expected =
      condition.value;

    if (operator === 'exists') {
      return !isEmpty(actual);
    }

    if (operator === 'not_exists') {
      return isEmpty(actual);
    }

    if (operator === 'truthy') {
      return Boolean(actual);
    }

    if (operator === 'falsy') {
      return !Boolean(actual);
    }

    if (operator === 'equals') {
      return valuesEqual(
        actual,
        expected
      );
    }

    if (operator === 'not_equals') {
      return !valuesEqual(
        actual,
        expected
      );
    }

    if (operator === 'in') {
      return (
        Array.isArray(expected) &&
        collectionContains(
          expected,
          actual
        )
      );
    }

    if (operator === 'not_in') {
      return !(
        Array.isArray(expected) &&
        collectionContains(
          expected,
          actual
        )
      );
    }

    if (operator === 'contains') {
      return collectionContains(
        actual,
        expected
      );
    }

    if (
      operator ===
      'not_contains'
    ) {
      return !collectionContains(
        actual,
        expected
      );
    }

    if (operator === 'starts_with') {
      return (
        typeof actual === 'string' &&
        actual.indexOf(
          String(expected)
        ) === 0
      );
    }

    if (operator === 'ends_with') {
      return (
        typeof actual === 'string' &&
        actual.slice(
          -String(expected).length
        ) === String(expected)
      );
    }

    if (operator === 'matches') {
      return matchesPattern(
        actual,
        expected,
        condition.flags
      );
    }

    return compareNumeric(
      actual,
      expected,
      operator
    );
  }

  function evaluateConditionList(
    conditions,
    mode,
    scope,
    warnings
  ) {
    if (!Array.isArray(conditions)) {
      return true;
    }

    if (conditions.length === 0) {
      return true;
    }

    if (mode === 'any') {
      return conditions.some(
        function (condition) {
          return evaluateCondition(
            condition,
            scope,
            warnings
          );
        }
      );
    }

    if (mode === 'none') {
      return conditions.every(
        function (condition) {
          return !evaluateCondition(
            condition,
            scope,
            warnings
          );
        }
      );
    }

    return conditions.every(
      function (condition) {
        return evaluateCondition(
          condition,
          scope,
          warnings
        );
      }
    );
  }

  function evaluateWhen(
    when,
    scope,
    warnings
  ) {
    if (
      !when ||
      (
        isPlainObject(when) &&
        Object.keys(when).length === 0
      )
    ) {
      return true;
    }

    if (Array.isArray(when)) {
      return evaluateConditionList(
        when,
        'all',
        scope,
        warnings
      );
    }

    if (!isPlainObject(when)) {
      warnings.push({
        code:
          'invalid_when_group'
      });

      return false;
    }

    const hasGroups =
      Array.isArray(when.all) ||
      Array.isArray(when.any) ||
      Array.isArray(when.none);

    if (!hasGroups) {
      return evaluateCondition(
        when,
        scope,
        warnings
      );
    }

    return (
      evaluateConditionList(
        when.all,
        'all',
        scope,
        warnings
      ) &&
      evaluateConditionList(
        when.any,
        'any',
        scope,
        warnings
      ) &&
      evaluateConditionList(
        when.none,
        'none',
        scope,
        warnings
      )
    );
  }

  function eventMatchesRule(
    rule,
    eventName
  ) {
    const events =
      rule.events ||
      rule.event ||
      '*';

    if (events === '*') {
      return true;
    }

    if (typeof events === 'string') {
      return events === eventName;
    }

    if (Array.isArray(events)) {
      return (
        events.indexOf('*') !== -1 ||
        events.indexOf(eventName) !== -1
      );
    }

    return false;
  }

  function normalizeActions(rule) {
    if (Array.isArray(rule.actions)) {
      return rule.actions;
    }

    if (isPlainObject(rule.action)) {
      return [rule.action];
    }

    if (typeof rule.action === 'string') {
      return [
        {
          type: rule.action
        }
      ];
    }

    return [];
  }

  function normalizeRule(
    rule,
    index,
    warnings
  ) {
    if (!isPlainObject(rule)) {
      warnings.push({
        code: 'invalid_rule',
        index: index
      });

      return null;
    }

    const id =
      normalizeString(
        rule.id ||
        'rule_' + (index + 1)
      );

    const actions =
      normalizeActions(rule);

    if (actions.length === 0) {
      warnings.push({
        code:
          'rule_actions_missing',
        rule_id: id
      });

      return null;
    }

    return {
      id: id,

      description:
        normalizeString(
          rule.description
        ),

      enabled:
        rule.enabled !== false,

      priority:
        normalizePriority(
          rule.priority
        ),

      events:
        clone(
          rule.events ||
          rule.event ||
          '*'
        ),

      when:
        clone(rule.when || {}),

      actions:
        clone(actions),

      stop:
        rule.stop === true,

      declaration_index:
        index
    };
  }

  function prepareRules(
    rules,
    warnings
  ) {
    if (!Array.isArray(rules)) {
      warnings.push({
        code:
          'rules_collection_invalid'
      });

      return [];
    }

    const limitedRules =
      rules.slice(0, MAX_RULES);

    if (rules.length > MAX_RULES) {
      warnings.push({
        code:
          'rules_collection_truncated',
        maximum: MAX_RULES,
        received: rules.length
      });
    }

    return limitedRules
      .map(function (rule, index) {
        return normalizeRule(
          rule,
          index,
          warnings
        );
      })
      .filter(function (rule) {
        return rule !== null;
      })
      .sort(function (left, right) {
        if (
          left.priority ===
          right.priority
        ) {
          return (
            left.declaration_index -
            right.declaration_index
          );
        }

        return (
          right.priority -
          left.priority
        );
      });
  }

  function addUnique(
    target,
    value
  ) {
    if (
      target.indexOf(value) === -1
    ) {
      target.push(value);
    }
  }

  function applyEnrichment(
    rule,
    action,
    result
  ) {
    const values =
      action.values ||
      action.data ||
      {};

    if (!isPlainObject(values)) {
      result.warnings.push({
        code:
          'rule_enrichment_invalid',
        rule_id: rule.id
      });

      return;
    }

    const mode =
      normalizeString(
        action.mode ||
        'set_if_missing'
      ).toLowerCase();

    Object.keys(values)
      .forEach(function (fieldName) {
        const before =
          result.enrichment[
            fieldName
          ];

        if (
          mode !== 'overwrite' &&
          typeof before !== 'undefined'
        ) {
          return;
        }

        const after =
          clone(values[fieldName]);

        result.enrichment[
          fieldName
        ] = after;

        result.enrichment_changes
          .push({
            rule_id: rule.id,
            field: fieldName,
            before:
              clone(before),
            after:
              clone(after),
            mode: mode
          });
      });
  }

  function applyRoutes(
    rule,
    action,
    result
  ) {
    let routes =
      action.routes ||
      action.providers ||
      action.channels ||
      [];

    if (typeof routes === 'string') {
      routes = [routes];
    }

    if (!Array.isArray(routes)) {
      result.warnings.push({
        code:
          'rule_routes_invalid',
        rule_id: rule.id
      });

      return;
    }

    routes.forEach(function (route) {
      const normalizedRoute =
        normalizeString(route)
          .toLowerCase();

      if (!normalizedRoute) {
        return;
      }

      addUnique(
        result.routes,
        normalizedRoute
      );
    });
  }

  function applyAction(
    rule,
    action,
    result
  ) {
    if (!isPlainObject(action)) {
      result.warnings.push({
        code:
          'invalid_rule_action',
        rule_id: rule.id
      });

      return {
        stop: false
      };
    }

    const type =
      normalizeString(
        action.type ||
        action.action
      ).toLowerCase();

    if (
      SUPPORTED_ACTIONS.indexOf(
        type
      ) === -1
    ) {
      result.warnings.push({
        code:
          'unsupported_rule_action',
        rule_id: rule.id,
        action: type
      });

      return {
        stop: false
      };
    }

    const decision = {
      rule_id: rule.id,
      action: type,
      reason:
        normalizeString(
          action.reason ||
          rule.description
        ),
      applied_at:
        new Date().toISOString()
    };

    if (type === 'enrich') {
      applyEnrichment(
        rule,
        action,
        result
      );

      result.decisions.push(
        decision
      );

      return {
        stop:
          action.stop === true
      };
    }

    if (type === 'route') {
      applyRoutes(
        rule,
        action,
        result
      );

      result.decisions.push(
        decision
      );

      return {
        stop:
          action.stop === true
      };
    }

    if (type === 'allow') {
      if (!result.blocked) {
        result.allowed = true;
        result.explicit_decision =
          'allow';

        if (decision.reason) {
          result.reason =
            decision.reason;
        }
      }

      result.decisions.push(
        decision
      );

      return {
        stop:
          action.stop === true
      };
    }

    result.allowed = false;
    result.blocked = true;
    result.explicit_decision =
      'block';
    result.reason =
      decision.reason ||
      'blocked_by_rule';

    result.decisions.push(
      decision
    );

    return {
      stop:
        action.stop !== false
    };
  }

  function validateRules(rules) {
    const warnings = [];

    const prepared =
      prepareRules(
        clone(rules || []),
        warnings
      );

    return {
      valid:
        warnings.filter(
          function (warning) {
            return (
              warning.code ===
                'invalid_rule' ||
              warning.code ===
                'rule_actions_missing' ||
              warning.code ===
                'rules_collection_invalid'
            );
          }
        ).length === 0,

      rule_count:
        prepared.length,

      rules:
        prepared,

      warnings:
        warnings
    };
  }

  function evaluate(
    eventName,
    payload,
    options
  ) {
    const token =
      performanceStart(
        'rules.total',
        {
          event: eventName
        }
      );

    const sourceOptions =
      isPlainObject(options)
        ? options
        : {};

    const rulesConfiguration =
      getRulesConfiguration();

    const result = {
      success: false,

      event:
        normalizeString(eventName),

      allowed: false,

      blocked: false,

      explicit_decision: '',

      reason: '',

      payload:
        clone(payload || {}),

      enrichment: {},

      enrichment_changes: [],

      routes: [],

      matched_rules: [],

      decisions: [],

      warnings: [],

      errors: [],

      evaluated_at:
        new Date().toISOString()
    };

    if (!result.event) {
      result.errors.push({
        code:
          'rules_event_name_missing'
      });

      reportError(
        'error',
        'rules_event_name_missing',
        'Rules Engine requires an event name.',
        {}
      );

      performanceEnd(
        token,
        {
          status: 'failed',
          reason:
            'event_name_missing'
        }
      );

      return result;
    }

    if (!isPlainObject(payload)) {
      result.errors.push({
        code:
          'rules_payload_invalid'
      });

      reportError(
        'error',
        'rules_payload_invalid',
        'Rules Engine requires a plain object payload.',
        {
          event: result.event
        }
      );

      performanceEnd(
        token,
        {
          event: result.event,
          status: 'failed',
          reason:
            'payload_invalid'
        }
      );

      return result;
    }

    const defaultDecision =
      normalizeDecision(
        sourceOptions
          .defaultDecision ||
        sourceOptions
          .default_decision ||
        rulesConfiguration
          .defaultDecision
      );

    result.allowed =
      defaultDecision === 'allow';

    result.blocked =
      defaultDecision === 'block';

    result.reason =
      result.blocked
        ? 'default_block'
        : 'default_allow';

    if (
      rulesConfiguration.enabled ===
        false &&
      !Array.isArray(
        sourceOptions.rules
      )
    ) {
      result.success = true;

      emitEvent(
        'event_rules_evaluated',
        {
          event: result.event,
          allowed: result.allowed,
          blocked: result.blocked,
          reason:
            'rules_disabled',
          matched_rules: [],
          routes: [],
          enrichment_fields: [],
          warnings: 0,
          errors: 0
        }
      );

      performanceEnd(
        token,
        {
          event: result.event,
          status: 'completed',
          rules_enabled: false,
          allowed: result.allowed
        }
      );

      return result;
    }

    const sourceRules =
      Array.isArray(
        sourceOptions.rules
      )
        ? sourceOptions.rules
        : rulesConfiguration
            .definitions;

    const rules =
      prepareRules(
        clone(sourceRules),
        result.warnings
      );

    const scope = {
      event: result.event,
      payload: result.payload,
      context:
        isPlainObject(
          sourceOptions.context
        )
          ? clone(
              sourceOptions.context
            )
          : {}
    };

    let stopEvaluation = false;

    for (
      let index = 0;
      index < rules.length;
      index += 1
    ) {
      const rule =
        rules[index];

      if (
        !rule.enabled ||
        !eventMatchesRule(
          rule,
          result.event
        )
      ) {
        continue;
      }

      const matched =
        evaluateWhen(
          rule.when,
          scope,
          result.warnings
        );

      if (!matched) {
        continue;
      }

      result.matched_rules.push({
        id: rule.id,
        priority: rule.priority,
        description:
          rule.description
      });

      for (
        let actionIndex = 0;
        actionIndex <
          rule.actions.length;
        actionIndex += 1
      ) {
        const actionResult =
          applyAction(
            rule,
            rule.actions[
              actionIndex
            ],
            result
          );

        if (actionResult.stop) {
          stopEvaluation = true;
          break;
        }
      }

      if (
        stopEvaluation ||
        rule.stop === true
      ) {
        break;
      }
    }

    result.success =
      result.errors.length === 0;

    emitEvent(
      'event_rules_evaluated',
      {
        event: result.event,
        allowed: result.allowed,
        blocked: result.blocked,
        reason: result.reason,
        matched_rules:
          result.matched_rules.map(
            function (rule) {
              return rule.id;
            }
          ),
        decisions:
          result.decisions.map(
            function (decision) {
              return {
                rule_id:
                  decision.rule_id,
                action:
                  decision.action,
                reason:
                  decision.reason
              };
            }
          ),
        routes:
          clone(result.routes),
        enrichment_fields:
          Object.keys(
            result.enrichment
          ),
        warnings:
          result.warnings.length,
        errors:
          result.errors.length
      }
    );

    debugLog(
      'Rules evaluated',
      {
        event: result.event,
        allowed: result.allowed,
        blocked: result.blocked,
        reason: result.reason,
        matchedRules:
          result.matched_rules.length,
        decisions:
          result.decisions.length,
        routes:
          result.routes,
        enrichment:
          result.enrichment,
        warnings:
          result.warnings.length
      }
    );

    performanceEnd(
      token,
      {
        event: result.event,
        status:
          result.success
            ? 'completed'
            : 'failed',
        allowed: result.allowed,
        blocked: result.blocked,
        matched_rules:
          result.matched_rules.length,
        decisions:
          result.decisions.length,
        warnings:
          result.warnings.length,
        errors:
          result.errors.length
      }
    );

    return result;
  }

  function printResult(result) {
    const source =
      result || {};

    const method =
      source.blocked
        ? 'warn'
        : 'info';

    console[method](
      '[Unified Rules Engine] Result',
      {
        event: source.event,
        success: source.success,
        allowed: source.allowed,
        blocked: source.blocked,
        reason: source.reason,
        matchedRules:
          source.matched_rules || [],
        decisions:
          source.decisions || [],
        routes:
          source.routes || [],
        enrichment:
          source.enrichment || {},
        warnings:
          source.warnings || [],
        errors:
          source.errors || []
      }
    );

    return source;
  }

  window.UnifiedRulesEngine = {
    version: VERSION,

    evaluate: evaluate,

    validateRules:
      validateRules,

    getConfiguredRules:
      getConfiguredRules,

    getSupportedActions:
      function () {
        return clone(
          SUPPORTED_ACTIONS
        );
      },

    getSupportedOperators:
      function () {
        return clone(
          SUPPORTED_OPERATORS
        );
      },

    getConfiguration:
      function () {
        return clone(
          getRulesConfiguration()
        );
      },

    printResult:
      printResult
  };

  if (
    getConfig().debug
  ) {
    const configuration =
      getRulesConfiguration();

    console.info(
      '[Unified Rules Engine] Rules Engine initialized',
      {
        version: VERSION,
        enabled:
          configuration.enabled,
        defaultDecision:
          configuration
            .defaultDecision,
        configuredRules:
          configuration
            .definitions.length,
        supportedActions:
          SUPPORTED_ACTIONS.length,
        supportedOperators:
          SUPPORTED_OPERATORS.length
      }
    );
  }
})();
